import { chainRunsApi } from '@/api/chainRuns'
import type { ChainRunFilters, PaginationParams } from '@/types'
import { isActiveChainRunStatus } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenantKey } from './useTenantKey'

export function useChainRuns(projectId: string, pagination: PaginationParams, filters?: ChainRunFilters) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chain-runs', { ...filters, ...pagination }),
    queryFn: () => chainRunsApi.getAll(projectId, filters, pagination),
    enabled: !!projectId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data?.items) return false
      const hasActiveRuns = data.items.some(r => isActiveChainRunStatus(r.status))
      return hasActiveRuns ? 3000 : false
    },
  })
}

export function useChainRunsForWorkItem(projectId: string, workItemId: string) {
  return useChainRuns(
    projectId,
    { maxResultCount: 50, skipCount: 0 },
    { search: '', status: '', chainDefinitionId: '', linkedEntityId: workItemId },
  )
}

export function useChainRun(projectId: string, chainRunId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chain-runs', chainRunId!),
    queryFn: () => chainRunsApi.getById(projectId, chainRunId!),
    enabled: !!projectId && !!chainRunId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      return isActiveChainRunStatus(data.status) ? 2000 : false
    },
  })
}

export function useAdvanceChainRun(projectId: string, chainRunId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (inputValuesJson?: string) => chainRunsApi.advance(projectId, chainRunId, inputValuesJson),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chain-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}
